import type { WorkspaceNode } from "./WorkPanel";
import type { CanvasPlacement, LifeSelection } from "./life/types";

export type SelectedTargetIdentityKind = "canonical" | "implicit" | "label-only";

export interface SelectedTarget {
  label?: string;
  selector?: string;
  surface?: string;
  objectId?: string;
  objectKind?: string;
  objectType?: string;
  fileId?: string;
  actionId?: string;
  messageId?: string;
  conversationId?: string;
  resourceUri?: string;
  nodeId?: string;
  lifeItemId?: string;
  appId?: string;
  identityKind?: SelectedTargetIdentityKind;
}

function asTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeTarget(target: SelectedTarget): SelectedTarget | undefined {
  const candidate: SelectedTarget = {
    label: asTrimmedString(target.label),
    selector: asTrimmedString(target.selector),
    surface: asTrimmedString(target.surface),
    objectId: asTrimmedString(target.objectId),
    objectKind: asTrimmedString(target.objectKind),
    fileId: asTrimmedString(target.fileId),
    actionId: asTrimmedString(target.actionId),
    messageId: asTrimmedString(target.messageId),
    conversationId: asTrimmedString(target.conversationId),
    resourceUri: asTrimmedString(target.resourceUri),
    nodeId: asTrimmedString(target.nodeId),
    lifeItemId: asTrimmedString(target.lifeItemId),
    appId: asTrimmedString(target.appId),
  };

  if (!Object.values(candidate).some(Boolean)) return undefined;

  // Strip undefined fields so consumers see only the asserted dimensions
  // — important for chat-runtime context which serializes the target into
  // the agent prompt envelope (extra `undefined` keys produce noisy
  // payloads and break deep-equal assertions).
  const cleaned: SelectedTarget = {};
  for (const [key, value] of Object.entries(candidate)) {
    if (value !== undefined) {
      (cleaned as Record<string, unknown>)[key] = value;
    }
  }
  cleaned.identityKind = hasCanonicalSelectedTargetIdentity(cleaned)
    ? "canonical"
    : asTrimmedString(cleaned.selector)
      ? "implicit"
      : "label-only";
  return cleaned;
}

export function normalizeSelectedTarget(target: SelectedTarget): SelectedTarget {
  const out: SelectedTarget = {
    ...target,
    objectType: target.objectType?.trim() || undefined,
    label: target.label?.trim() || undefined,
  };
  // Stamp identityKind so downstream consumers (UI bridge, chat-runtime,
  // audit log) can tell whether the target is canonically anchored to a
  // durable id, derived from a UI selector, or just a label.
  if (out.identityKind === undefined) {
    out.identityKind = hasCanonicalSelectedTargetIdentity(out)
      ? "canonical"
      : asTrimmedString(out.selector)
        ? "implicit"
        : "label-only";
  }
  return out;
}

export function hasCanonicalSelectedTargetIdentity(target: SelectedTarget | undefined): boolean {
  if (!target) return false;
  return [
    target.objectId,
    target.fileId,
    target.actionId,
    target.messageId,
    target.conversationId,
    target.resourceUri,
  ].some((value) => asTrimmedString(value) !== undefined);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function hasImplicitTargetAnchor(context?: Record<string, unknown>): boolean {
  if (!context) return false;

  return [
    context.uiElementSelector,
    context.objectId,
    context.objectKind,
    context.fileId,
    context.actionId,
    context.messageId,
    context.conversationId,
    context.resourceUri,
    context.sidebarNodeId,
    context.lifeItemId,
    context.appId,
  ].some((value) => asTrimmedString(value) !== undefined);
}

export function extractSelectedTargetFromContext(
  context?: Record<string, unknown>,
): SelectedTarget | undefined {
  const explicit = normalizeTarget((asRecord(context?.selectedTarget) ?? {}) as SelectedTarget);
  if (explicit) return explicit;

  // Keep page-level bridge context generic unless the request includes a concrete
  // target anchor such as a selector, node id, or provenance id.
  if (!hasImplicitTargetAnchor(context)) {
    return undefined;
  }

  return normalizeTarget({
    label:
      asTrimmedString(context?.uiElementLabel) ??
      asTrimmedString(context?.lifeItemLabel) ??
      asTrimmedString(context?.sidebarNodeLabel),
    selector: asTrimmedString(context?.uiElementSelector),
    surface: asTrimmedString(context?.uiSurface),
    objectId: asTrimmedString(context?.objectId),
    objectKind: asTrimmedString(context?.objectKind),
    fileId: asTrimmedString(context?.fileId),
    actionId: asTrimmedString(context?.actionId),
    messageId: asTrimmedString(context?.messageId),
    conversationId: asTrimmedString(context?.conversationId),
    resourceUri: asTrimmedString(context?.resourceUri),
    nodeId: asTrimmedString(context?.sidebarNodeId),
    lifeItemId: asTrimmedString(context?.lifeItemId),
    appId: asTrimmedString(context?.appId),
  });
}

export function buildSelectedTargetSummary(target: SelectedTarget | undefined): string | undefined {
  if (!target) return undefined;

  const parts: string[] = [];
  if (target.label) {
    parts.push(`Selected target: ${target.label}.`);
  }
  if (target.surface) {
    parts.push(`Target surface: ${target.surface}.`);
  }

  const ids: string[] = [];
  if (target.objectId) ids.push(`object ${target.objectId}`);
  if (target.objectKind) ids.push(`kind ${target.objectKind}`);
  if (target.fileId) ids.push(`file ${target.fileId}`);
  if (target.actionId) ids.push(`action ${target.actionId}`);
  if (target.messageId) ids.push(`message ${target.messageId}`);
  if (target.conversationId) ids.push(`conversation ${target.conversationId}`);
  if (target.resourceUri) ids.push(`resource ${target.resourceUri}`);

  if (ids.length > 0) {
    parts.push(`Traceable target ids: ${ids.join(", ")}.`);
  }

  const localAnchors: string[] = [];
  if (target.nodeId) localAnchors.push(`node ${target.nodeId}`);
  if (target.lifeItemId) localAnchors.push(`life item ${target.lifeItemId}`);
  if (target.appId) localAnchors.push(`app ${target.appId}`);
  if (localAnchors.length > 0) {
    parts.push(`Local selection anchor: ${localAnchors.join(", ")}.`);
  }

  return parts.length > 0 ? parts.join(" ") : undefined;
}

export function buildSelectedTargetProvenance(
  target: SelectedTarget | undefined,
): Record<string, unknown> | undefined {
  if (!hasCanonicalSelectedTargetIdentity(target)) return undefined;
  const provenance = {
    conversationId: target.conversationId,
    messageId: target.messageId,
    actionId: target.actionId,
    objectId: target.objectId,
    objectKind: target.objectKind,
    fileId: target.fileId,
    resourceUri: target.resourceUri,
    source: target.surface ?? "workspace-ui",
  };
  return Object.values(provenance).some(Boolean) ? provenance : undefined;
}

export function extractSelectedTargetFromElement(
  el: HTMLElement,
  params?: { label?: string; surface?: string; selector?: string; appId?: string },
): SelectedTarget | undefined {
  return normalizeTarget({
    label: params?.label,
    selector: params?.selector,
    surface: params?.surface,
    objectId: el.dataset.objectId,
    objectKind: el.dataset.objectKind,
    fileId: el.dataset.fileId,
    actionId: el.dataset.actionId,
    messageId: el.dataset.messageId,
    conversationId: el.dataset.conversationId,
    resourceUri: el.dataset.resourceUri,
    nodeId: el.dataset.nodeId,
    lifeItemId: el.dataset.lifeItemId,
    appId: params?.appId ?? el.dataset.appId,
  });
}

function mapWorkspaceNodeKind(node: WorkspaceNode): string | undefined {
  switch (node.type) {
    case "file":
      return "file";
    case "project":
      return "project";
    case "workstream":
      return "workstream";
    case "issue":
      return "issue";
    case "app":
      return "app";
    case "life":
      return "life_item";
    case "action_group":
      return "action_group";
    case "category":
      // Categories are sidebar-organizational nodes — not domain
      // objects. They get a `nodeId` but no `objectKind`. Returning
      // undefined here is intentional; caller passes through metadata.
      return undefined;
    default:
      return undefined;
  }
}

export function buildSelectedTargetFromWorkspaceNode(
  node: WorkspaceNode,
  surface = "sidebar",
): SelectedTarget | undefined {
  if (node.type === "category") {
    return normalizeTarget({
      label: node.label,
      surface,
      nodeId: node.id,
    });
  }

  const metadata = (node.metadata ?? {}) as Record<string, unknown>;
  const objectId = asTrimmedString(metadata.objectId);
  const fileId = asTrimmedString(metadata.fileId);
  const conversationId =
    asTrimmedString(metadata.conversationId) ?? asTrimmedString(metadata.sessionId);

  return normalizeTarget({
    label: node.label,
    surface,
    objectId,
    objectKind: asTrimmedString(metadata.objectKind) ?? mapWorkspaceNodeKind(node),
    fileId,
    actionId: asTrimmedString(metadata.actionId),
    messageId: asTrimmedString(metadata.messageId),
    conversationId,
    resourceUri: asTrimmedString(metadata.resourceUri),
    nodeId: node.id,
    appId: node.type === "app" ? node.id : undefined,
  });
}

export function buildSelectedTargetFromLifePlacement(
  placement: CanvasPlacement,
  label: string,
): SelectedTarget | undefined {
  const metadata = (placement.metadata ?? {}) as Record<string, unknown>;
  return normalizeTarget({
    label,
    surface: "life",
    objectId: asTrimmedString(metadata.objectId),
    objectKind: asTrimmedString(metadata.objectKind) ?? placement.primitiveType,
    fileId: asTrimmedString(metadata.fileId),
    actionId: asTrimmedString(metadata.actionId),
    messageId: asTrimmedString(metadata.messageId),
    conversationId: asTrimmedString(metadata.conversationId),
    resourceUri: asTrimmedString(metadata.resourceUri),
    lifeItemId: placement.itemId,
  });
}

/**
 * Promote a LifeSelection (the chip-style selection a user collects on the
 * Life canvas) into a canonical SelectedTarget. Used by chat-runtime to
 * auto-merge selections into the outbound selectedTarget when the caller
 * didn't pass one explicitly. Phase 0A delivers "click a Life item, agent
 * receives canonical ObjectRef" without requiring callers to re-thread the
 * selection separately.
 */
export function buildSelectedTargetFromLifeSelection(
  selection: LifeSelection,
): SelectedTarget | undefined {
  return normalizeTarget({
    label: selection.title,
    surface: "life",
    // LifeSelection carries primitiveType ("event" / "goal" / "item" /
    // "person" / "reminder" / ...) — use it as the ontology kind anchor.
    objectKind: selection.primitiveType,
    lifeItemId: selection.itemId,
  });
}

// ---------------------------------------------------------------------------
// MethodContext / runtime ObjectRef projection
// ---------------------------------------------------------------------------

/**
 * Server-side MethodContext.SelectedObject shape. Mirrored here (rather than
 * imported from src/runtime) because hank/src/ui code must compile in the
 * UI package without dragging the runtime's deeper type tree. A
 * drift-guard test in chat-runtime ensures the shape stays in sync.
 */
export interface SelectedObjectRef {
  kind: string;
  id: string;
  surface: string;
  label?: string;
  related?: {
    fileId?: string;
    actionId?: string;
    messageId?: string;
    conversationId?: string;
    resourceUri?: string;
  };
}

export interface SelectedObjectProjection {
  ref: SelectedObjectRef;
  identityKind: SelectedTargetIdentityKind;
}

/**
 * Convert a SelectedTarget into the MethodContext-shaped SelectedObject. The
 * id-resolution preference mirrors `hasCanonicalSelectedTargetIdentity()`:
 * objectId > fileId > actionId > messageId > conversationId > resourceUri >
 * lifeItemId > nodeId > appId. Returns undefined if no usable id exists.
 */
export function selectedTargetToObjectRef(
  target: SelectedTarget | undefined,
): SelectedObjectProjection | undefined {
  if (!target) return undefined;
  const id =
    asTrimmedString(target.objectId) ??
    asTrimmedString(target.fileId) ??
    asTrimmedString(target.actionId) ??
    asTrimmedString(target.messageId) ??
    asTrimmedString(target.conversationId) ??
    asTrimmedString(target.resourceUri) ??
    asTrimmedString(target.lifeItemId) ??
    asTrimmedString(target.nodeId) ??
    asTrimmedString(target.appId);
  if (!id) return undefined;

  const kind =
    asTrimmedString(target.objectKind) ?? asTrimmedString(target.objectType) ?? "unknown";
  const surface = asTrimmedString(target.surface) ?? "workspace";

  // Always assign trimmed values — `id` already does this, so doing the same
  // for `label` and `related.*` keeps the projection internally consistent
  // when callers feed in raw / deserialized SelectedTarget objects that
  // didn't go through normalizeTarget first.
  const related: SelectedObjectRef["related"] = {};
  const trimmedFileId = asTrimmedString(target.fileId);
  const trimmedActionId = asTrimmedString(target.actionId);
  const trimmedMessageId = asTrimmedString(target.messageId);
  const trimmedConversationId = asTrimmedString(target.conversationId);
  const trimmedResourceUri = asTrimmedString(target.resourceUri);
  if (trimmedFileId) related.fileId = trimmedFileId;
  if (trimmedActionId) related.actionId = trimmedActionId;
  if (trimmedMessageId) related.messageId = trimmedMessageId;
  if (trimmedConversationId) related.conversationId = trimmedConversationId;
  if (trimmedResourceUri) related.resourceUri = trimmedResourceUri;

  const trimmedLabel = asTrimmedString(target.label);
  const ref: SelectedObjectRef = {
    kind,
    id,
    surface,
    ...(trimmedLabel ? { label: trimmedLabel } : {}),
    ...(Object.keys(related).length > 0 ? { related } : {}),
  };
  const normalized = normalizeSelectedTarget(target);
  return {
    ref,
    identityKind: normalized.identityKind ?? "label-only",
  };
}
